import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Switch,
  Text
} from '@chakra-ui/react'
import { KeyboardEvent, PointerEvent, useEffect, useMemo, useRef, useState } from 'react'

import { FormField } from '../../../../components/FormField'
import { FormSelect } from '../../../../components/Select'
import {
  CertificateAlign,
  CertificateField,
  CertificateFieldKey,
  CertificateFontKey,
  CertificateLayout
} from '../../../../services/Event/types'
import {
  CERTIFICATE_FIELD_LABELS,
  CERTIFICATE_FIELDS,
  CERTIFICATE_FONTS,
  drawFields,
  IMeasuredField,
  IPdfFirstPage,
  loadCertificateFonts,
  measureField
} from './certificateRendering'

interface ICertificateLayoutEditorProps {
  page: IPdfFirstPage
  layout: CertificateLayout
  defaultLayout: CertificateLayout
  /** O texto de exemplo de cada campo — o que a prévia escreve. */
  texts: Record<CertificateFieldKey, string>
  onChange: (layout: CertificateLayout) => void
  isDisabled?: boolean
}

type TDragMode = 'move' | 'left' | 'right'

interface IDragState {
  key: CertificateFieldKey
  mode: TDragMode
  pointerX: number
  pointerY: number
  /** px de tela por pt, medido no início do arraste. */
  scale: number
  field: CertificateField
}

const MIN_WIDTH = 20

/**
 * Quanto a largura precisa mudar para a página ser redesenhada. Abaixo disso é a barra de rolagem
 * do modal aparecendo e sumindo: a página encolhe, a altura cai, a barra some, a página cresce — e
 * redesenhar a cada vai e volta travava a aba com um modelo pesado.
 */
const RESIZE_THRESHOLD = 32

const ALIGN_OPTIONS: { value: CertificateAlign; label: string }[] = [
  { value: 'left', label: 'Esquerda' },
  { value: 'center', label: 'Centro' },
  { value: 'right', label: 'Direita' }
]

const FONT_OPTIONS = (Object.keys(CERTIFICATE_FONTS) as CertificateFontKey[]).map((font) => ({
  value: font,
  label: CERTIFICATE_FONTS[font].label
}))

const round1 = (value: number) => Math.round(value * 10) / 10

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

/**
 * Editor visual do certificado: a 1ª página do modelo com o nome do participante por cima.
 *
 * Arrastar a caixa move o texto; as alças laterais mudam a largura máxima (o texto mais largo que
 * ela encolhe). O painel ao lado ajusta o campo selecionado, e as setas do teclado movem a caixa
 * de 1 pt (10 pt com Shift).
 */
export default function CertificateLayoutEditor({
  page,
  layout,
  defaultLayout,
  texts,
  onChange,
  isDisabled = false
}: ICertificateLayoutEditorProps) {
  const stageRef = useRef<HTMLDivElement>(null)
  const pageCanvasRef = useRef<HTMLCanvasElement>(null)
  const textCanvasRef = useRef<HTMLCanvasElement>(null)
  const dragRef = useRef<IDragState | null>(null)
  // Só a resolução dos canvas depende disto: as caixas são posicionadas em % da página.
  const [stageWidth, setStageWidth] = useState(0)
  const [fontsReady, setFontsReady] = useState(false)
  const [selected, setSelected] = useState<CertificateFieldKey>('name')

  useEffect(() => {
    let active = true
    loadCertificateFonts().then(() => {
      if (active) setFontsReady(true)
    })

    return () => {
      active = false
    }
  }, [])

  // A página acompanha a largura do modal.
  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return undefined

    let timer: number | undefined
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.floor(entry.contentRect.width)
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        setStageWidth((current) => (Math.abs(width - current) >= RESIZE_THRESHOLD ? width : current))
      }, 150)
    })
    observer.observe(stage)

    return () => {
      window.clearTimeout(timer)
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!pageCanvasRef.current || stageWidth === 0) return
    page.render(pageCanvasRef.current, stageWidth).catch(() => undefined)
  }, [page, stageWidth])

  const measured = useMemo(
    () =>
      CERTIFICATE_FIELDS.reduce(
        (all, key) => ({ ...all, [key]: measureField(layout[key], texts[key]) }),
        {} as Record<CertificateFieldKey, IMeasuredField>
      ),
    // `fontsReady` refaz a medida quando os TTFs chegam — antes disso a largura é a da fonte padrão.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layout, texts, fontsReady]
  )

  useEffect(() => {
    if (!textCanvasRef.current || stageWidth === 0) return
    drawFields(textCanvasRef.current, stageWidth, page, layout, measured)
  }, [layout, measured, page, stageWidth])

  const updateField = (key: CertificateFieldKey, changes: Partial<CertificateField>) => {
    onChange({ ...layout, [key]: { ...layout[key], ...changes } })
  }

  const startDrag = (key: CertificateFieldKey, mode: TDragMode) => (event: PointerEvent<HTMLElement>) => {
    setSelected(key)
    if (isDisabled || event.button !== 0) return

    event.preventDefault()
    event.stopPropagation()
    const scale = (stageRef.current?.getBoundingClientRect().width ?? 0) / page.width
    if (scale === 0) return

    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = { key, mode, pointerX: event.clientX, pointerY: event.clientY, scale, field: layout[key] }
  }

  const onDrag = (event: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current
    if (!drag) return

    const dx = (event.clientX - drag.pointerX) / drag.scale
    const dy = (event.clientY - drag.pointerY) / drag.scale
    const { field } = drag

    if (drag.mode === 'move') {
      updateField(drag.key, {
        x: round1(clamp(field.x + dx, 0, page.width)),
        y: round1(clamp(field.y + dy, 0, page.height))
      })
      return
    }

    // A alça puxa a borda; num texto centralizado as duas bordas andam juntas. A largura não passa
    // da página — a API recusa um layout assim.
    const growth = drag.mode === 'right' ? dx : -dx
    const factor = field.align === 'center' ? 2 : 1
    updateField(drag.key, { max_width: round1(clamp(field.max_width + growth * factor, MIN_WIDTH, page.width)) })
  }

  const endDrag = (event: PointerEvent<HTMLElement>) => {
    if (!dragRef.current) return
    dragRef.current = null
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  const onKeyDown = (key: CertificateFieldKey) => (event: KeyboardEvent<HTMLElement>) => {
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1]
    }
    const move = moves[event.key]
    if (!move || isDisabled) return

    event.preventDefault()
    const step = event.shiftKey ? 10 : 1
    updateField(key, {
      x: round1(clamp(layout[key].x + move[0] * step, 0, page.width)),
      y: round1(clamp(layout[key].y + move[1] * step, 0, page.height))
    })
  }

  const field = layout[selected]

  return (
    <SimpleGrid templateColumns={{ base: '1fr', lg: 'minmax(0, 1fr) 280px' }} gap={5} alignItems="start">
      <Box>
        <Box
          ref={stageRef}
          position="relative"
          w="100%"
          borderWidth="1px"
          borderColor="gray.200"
          bg="gray.50"
          userSelect="none"
          style={{ aspectRatio: `${page.width} / ${page.height}` }}
        >
          <canvas ref={pageCanvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
          <canvas
            ref={textCanvasRef}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
          />

          {CERTIFICATE_FIELDS.map((key) => {
            if (!layout[key].enabled) return null

            const box = measured[key]
            const isSelected = key === selected
            const handles: TDragMode[] =
              layout[key].align === 'center' ? ['left', 'right'] : layout[key].align === 'left' ? ['right'] : ['left']

            return (
              <Box
                key={key}
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                aria-label={`${CERTIFICATE_FIELD_LABELS[key]} — arraste para mover`}
                position="absolute"
                left={`${(box.rangeLeft / page.width) * 100}%`}
                top={`${((layout[key].y - box.ascent) / page.height) * 100}%`}
                w={`${(layout[key].max_width / page.width) * 100}%`}
                h={`${((box.ascent + box.descent) / page.height) * 100}%`}
                border="1px dashed"
                borderColor={isSelected ? 'magenta.500' : 'transparent'}
                cursor={isDisabled ? 'default' : 'move'}
                _hover={{ borderColor: isSelected ? 'magenta.500' : 'gray.400' }}
                _focusVisible={{ outline: '2px solid', outlineColor: 'magenta.500' }}
                style={{ touchAction: 'none' }}
                onPointerDown={startDrag(key, 'move')}
                onPointerMove={onDrag}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onFocus={() => setSelected(key)}
                onKeyDown={onKeyDown(key)}
              >
                {isSelected &&
                  !isDisabled &&
                  handles.map((mode) => (
                    <Box
                      key={mode}
                      aria-hidden
                      position="absolute"
                      top="50%"
                      {...(mode === 'left' ? { left: '-5px' } : { right: '-5px' })}
                      w="10px"
                      h="22px"
                      mt="-11px"
                      bg="magenta.500"
                      borderRadius="sm"
                      cursor="ew-resize"
                      onPointerDown={startDrag(key, mode)}
                      onPointerMove={onDrag}
                      onPointerUp={endDrag}
                      onPointerCancel={endDrag}
                    />
                  ))}
              </Box>
            )
          })}
        </Box>
        <Text variant="caption" mt={2}>
          Arraste os textos para posicioná-los; as alças laterais definem a largura máxima (textos mais longos diminuem
          para caber). Setas do teclado movem 1 pt, com Shift 10 pt.
        </Text>
      </Box>

      <Flex direction="column" gap={4}>
        <Flex align="center" justify="space-between">
          <Text fontWeight="semibold" fontSize="sm">
            {CERTIFICATE_FIELD_LABELS[selected]}
          </Text>
          <Switch
            aria-label="Escrever este texto no certificado"
            isChecked={field.enabled}
            isDisabled={isDisabled}
            onChange={(e) => updateField(selected, { enabled: e.target.checked })}
          />
        </Flex>

        {/* O nome é o único texto escrito: desligado, todo participante recebe o mesmo PDF. */}
        {!layout.name.enabled && (
          <Alert status="warning" borderRadius="md" fontSize="sm">
            <AlertIcon />O nome não vai ser escrito, e todos os participantes vão receber o mesmo certificado.
          </Alert>
        )}

        <FormField label="Fonte">
          <FormSelect
            value={field.font}
            onChange={(value) => updateField(selected, { font: value as CertificateFontKey })}
            options={FONT_OPTIONS}
            hideEmptyOption
            isDisabled={isDisabled || !field.enabled}
          />
        </FormField>

        <SimpleGrid columns={2} gap={3}>
          <FormField label="Tamanho (pt)">
            <PtInput
              value={field.size}
              min={4}
              max={200}
              isDisabled={isDisabled || !field.enabled}
              onChange={(size) => updateField(selected, { size })}
            />
          </FormField>
          <FormField label="Cor">
            <Input
              type="color"
              p={0}
              w="100%"
              value={field.color}
              isDisabled={isDisabled || !field.enabled}
              onChange={(e) => updateField(selected, { color: e.target.value.toUpperCase() })}
            />
          </FormField>
        </SimpleGrid>

        <FormField label="Alinhamento">
          <ButtonGroup size="sm" isAttached variant="outline" w="100%" isDisabled={isDisabled || !field.enabled}>
            {ALIGN_OPTIONS.map((option) => (
              <Button
                key={option.value}
                flex="1"
                isActive={field.align === option.value}
                onClick={() => updateField(selected, { align: option.value })}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
        </FormField>

        <Flex align="center" justify="space-between">
          <Text fontSize="sm">Letras maiúsculas</Text>
          <Switch
            aria-label="Letras maiúsculas"
            isChecked={field.uppercase}
            isDisabled={isDisabled || !field.enabled}
            onChange={(e) => updateField(selected, { uppercase: e.target.checked })}
          />
        </Flex>

        <SimpleGrid columns={3} gap={2}>
          <FormField label="X (pt)">
            <PtInput
              value={field.x}
              min={0}
              max={page.width}
              isDisabled={isDisabled || !field.enabled}
              onChange={(x) => updateField(selected, { x })}
            />
          </FormField>
          <FormField label="Y (pt)">
            <PtInput
              value={field.y}
              min={0}
              max={page.height}
              isDisabled={isDisabled || !field.enabled}
              onChange={(y) => updateField(selected, { y })}
            />
          </FormField>
          <FormField label="Largura">
            <PtInput
              value={field.max_width}
              min={MIN_WIDTH}
              max={page.width}
              isDisabled={isDisabled || !field.enabled}
              onChange={(max_width) => updateField(selected, { max_width })}
            />
          </FormField>
        </SimpleGrid>

        {measured[selected].size < field.size && field.enabled && (
          <Text variant="caption">
            Com o texto de exemplo, a fonte diminui para {measured[selected].size.toLocaleString('pt-BR')} pt para caber
            na largura.
          </Text>
        )}

        <Button
          variant="outline"
          size="sm"
          alignSelf="flex-start"
          isDisabled={isDisabled}
          onClick={() => updateField(selected, defaultLayout[selected])}
        >
          Restaurar padrão
        </Button>
      </Flex>
    </SimpleGrid>
  )
}

interface IPtInputProps {
  value: number
  min: number
  max: number
  isDisabled?: boolean
  onChange: (value: number) => void
}

/**
 * Campo numérico em pt. Guarda o texto enquanto a pessoa digita ("12." ainda não é número) e só
 * repassa números válidos; fora de foco, acompanha o valor que vem do arraste.
 */
function PtInput({ value, min, max, isDisabled, onChange }: IPtInputProps) {
  const [text, setText] = useState(String(value))
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (!isFocused) setText(String(value))
  }, [value, isFocused])

  return (
    <NumberInput
      value={text}
      min={min}
      max={max}
      step={1}
      isDisabled={isDisabled}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onChange={(valueAsString, valueAsNumber) => {
        setText(valueAsString)
        if (Number.isFinite(valueAsNumber)) onChange(round1(valueAsNumber))
      }}
    >
      <NumberInputField px={2} />
      <NumberInputStepper>
        <NumberIncrementStepper />
        <NumberDecrementStepper />
      </NumberInputStepper>
    </NumberInput>
  )
}
